"use client"
import styled from "@emotion/styled"
import SearchIcon from "@mui/icons-material/Search"
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TabsProps,
  TextField,
} from "@mui/material"
import { useEffect, useState } from "react"
import SwiperOnMobile from "../SwiperOnMobile/SwiperOnMobile"
import { colors, mediaQueries, sizes } from "../theme/theme"

interface CustomTabBarProps {
  value: number | null
  handleChange: (event: React.SyntheticEvent, newValue: number) => void
  categories: string[]
  searchBarText?: string
  searchTerm?: string
  onSearchChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  isSearchOpen?: boolean
  onSearchToggle?: (isOpen: boolean) => void
}

const StyledFilters = styled(SwiperOnMobile)<TabsProps>`
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  margin: ${sizes.baseSingle} 0px 40px 0px;
  gap: ${sizes.baseDouble};
  width: 100%;
  max-width: 100%;
  ${mediaQueries.tabletDown} {
    .swiper {
      .swiper-slide {
        width: fit-content;
      }
    }
  }
`

const StyledFilter = styled(Button, {
  shouldForwardProp: (prop) => prop !== "isSelected",
})<{ isSelected?: boolean }>`
  width: fit-content;
  &.MuiButton-root {
    flex-wrap: nowrap;
    justify-content: flex-start;
    white-space: nowrap;
    font-size: 14px;
    font-weight: 400;
    padding: 12px ${sizes.baseDouble};
    background-color: ${(props) =>
      props.isSelected
        ? colors.red
        : "transparent"} !important; // override MUI default
    color: ${(props) => (props.isSelected ? colors.white : "inherit")};
    margin: 0px;
    font-size: 14px;
    font-weight: 400;
    background-color: transparent;
    &:hover {
      background-color: rgba(211, 47, 47, 0.04);
    }
    ${mediaQueries.tabletDown} {
      justify-content: center;
      align-items: stretch;
    }
    &:hover,
    &:focus,
    &.Mui-selected {
      background-color: ${colors.red};
    }
  }
`

const StyledSearchField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "isSearchOpen",
})<{ isSearchOpen?: boolean }>`
  width: ${(prop) => (prop.isSearchOpen ? "250px" : "0")};
  opacity: ${(prop) => (prop.isSearchOpen ? "1" : "0")};
  transition:
    width 0.3s ease,
    opacity 0.5s ease;
  background-color: ${colors.white};
  border-radius: 3em;
  .MuiOutlinedInput-root {
    display: flex;
    align-items: center;
    font-size: 14px;
    padding: 0px;
    height: unset;
    input {
      border-radius: 3em;
      border: none;
      background-color: ${colors.white};
    }
    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: ${colors.red};
    }
    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: ${colors.red};
    }
  }
  ${mediaQueries.tabletDown} {
    width: 100%;
  }
`

const SearchIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "isSearchOpen",
})<{ isSearchOpen?: boolean }>`
  color: ${colors.red};
  padding: ${(prop) => (prop.isSearchOpen ? "0" : "12px")};
  border-radius: ${(prop) => (prop.isSearchOpen ? sizes.borderRadius : "50%")};
  border: 1px solid ${colors.red};
  &:hover {
    background-color: rgba(211, 47, 47, 0.04);
  }
`

const SearchContainer = styled(Box)`
  display: flex;
  align-items: center;
  ${mediaQueries.tabletDown} {
    width: 100%;
    margin-top: ${sizes.baseHalf};
  }
`

const FiltersBar: React.FC<CustomTabBarProps> = ({
  value,
  handleChange,
  categories,
  searchBarText,
  searchTerm = "",
  onSearchChange,
  isSearchOpen: externalSearchOpen,
  onSearchToggle,
}) => {
  const [internalSearchOpen, setInternalSearchOpen] = useState(false)

  // Determine which state to use - controlled or uncontrolled
  const isSearchOpen =
    externalSearchOpen !== undefined ? externalSearchOpen : internalSearchOpen

  const toggleSearch = () => {
    const newSearchState = !isSearchOpen

    // Update internal state if in uncontrolled mode
    if (externalSearchOpen === undefined) {
      setInternalSearchOpen(newSearchState)
    }

    // Notify parent component if callback exists
    if (onSearchToggle) {
      onSearchToggle(newSearchState)
    }
  }

  // Sync internal state with external state when controlled
  useEffect(() => {
    if (externalSearchOpen !== undefined) {
      setInternalSearchOpen(externalSearchOpen)
    }
  }, [externalSearchOpen])

  // Create an array of elements for the swiper
  const filterElements =
    categories
      ?.map((category, index) => {
        if (!category) return null
        return (
          <StyledFilter
            key={`${category}-${index}`}
            variant="outlined"
            onClick={(event) => handleChange(event, index)}
            isSelected={value === index}
          >
            {category}
          </StyledFilter>
        )
      })
      .filter(Boolean) || []

  // Create search element if needed
  const searchElement = searchBarText ? (
    <SearchContainer key="search-container">
      <SearchIconButton
        isSearchOpen={isSearchOpen}
        onClick={toggleSearch}
        aria-label="search"
      >
        {isSearchOpen ? (
          <StyledSearchField
            isSearchOpen={isSearchOpen}
            placeholder={searchBarText}
            value={searchTerm}
            onChange={onSearchChange}
            variant="outlined"
            size="small"
            autoFocus
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.red }} />
                </InputAdornment>
              ),
            }}
          />
        ) : (
          <SearchIcon />
        )}
      </SearchIconButton>
    </SearchContainer>
  ) : null

  // Combine filter elements and search element
  const allElements = searchElement
    ? [...filterElements, searchElement]
    : filterElements

  return (
    <StyledFilters
      value={value}
      swiperProps={{
        navigation: false,
        slidesPerView: "auto",
        slidesPerGroup: 1,
        centeredSlides: false,
        loop: false,
        freeMode: true,
      }}
    >
      {allElements}
    </StyledFilters>
  )
}

export default FiltersBar
